/**
 * Budget band — the wallet-scale axis, now expressed in real money.
 *
 * Why this exists:
 *   `constraint-priority.ts` asks "what is non-negotiable today" (Budget /
 *   School / Commute). Budget band is the wallet-scale dimension that
 *   composes with it. Internally the bands stay categorical (none / tight
 *   / moderate / stretch) so the classifier and shortlist scoring keep
 *   compiling — but the user sees real prices that match the active city,
 *   purchase mode, and property type. Users said "Tight / Moderate /
 *   Stretch" was meaningless; budget must be instantly understandable.
 *
 * What changed in Phase 6:
 *   - Pill labels now show real city-aware money (e.g. "A$900k–1.5M",
 *     "$550–700/wk") via `budget-format.ts`.
 *   - Mode (Buy / Rent) and Type (Any / House / Apartment) are exposed as
 *     two compact pill rows above the budget bands. Changing either re-
 *     paints the band labels live.
 *
 * Design rules:
 *   - Single global state, subscribers.
 *   - No new data; price ranges live in `budget-format.ts`.
 *   - Default = "none" → nothing changes for users who don't engage.
 *   - Rendering lives in the Decide surface (`buildModeRow`/`buildTypeRow`
 *     below + the budget-range-bar); nothing budget-flavoured mounts at
 *     boot (docs/ONE_STORY.md).
 */

import {
  budgetBucketDef,
  type CityCode,
  type BudgetBucket,
} from "./budget-format";
import {
  getPurchaseMode,
  setPurchaseMode,
  subscribePurchaseMode,
  type PurchaseMode,
} from "./purchase-mode";
import {
  getPropertyType,
  setPropertyType,
  subscribePropertyType,
  type PropertyType,
} from "./property-type";

export type BudgetBand = "none" | "tight" | "moderate" | "stretch";

/* -------------------------------------------------------------------------- */
/*  City-scoped storage                                                       */
/*                                                                            */
/*  Pre-2026-05: storage keys were global. Switching cities (HYD ↔ MEL ↔      */
/*  BLR ↔ SYD) bled the previous city's bucket + range into the next city's  */
/*  bar — and since `lo/hi` are stored in the SOURCE city's units (₹ Lakh    */
/*  vs A$ thousand vs $/wk), the next city's `bucketFit` gate read           */
/*  meaningless numbers and the price filter no-op'd. Concretely: a SYD     */
/*  user dragging to A$1M-3M then switching to BLR saw the bar snap to     */
/*  "₹2.6Cr-4Cr" but the gate checked `median <= 3000` (Lakh) — every BLR  */
/*  suburb passed.                                                           */
/*                                                                            */
/*  Post-fix: each storage key carries the city as a suffix                  */
/*  (`hyperlocal-budget-bucket:blr`). On first read for a new city we also      */
/*  migrate any legacy un-scoped key forward, so users upgrading don't lose  */
/*  their picks for the city they were last on.                              */
/* -------------------------------------------------------------------------- */

const STORAGE_KEY_BASE = "hyperlocal-budget-band";
const BUCKET_STORAGE_KEY_BASE = "hyperlocal-budget-bucket";
const RANGE_STORAGE_KEY_BASE = "hyperlocal-budget-range";

/** The city whose state these module-level vars currently mirror. `null`
 *  before `setBudgetCity()` runs (first import); reads/writes fall back to
 *  the legacy un-scoped keys so the persisted value before the upgrade
 *  doesn't vanish. */
let activeCity: CityCode | null = null;

function bandKey(): string {
  return activeCity ? `${STORAGE_KEY_BASE}:${activeCity}` : STORAGE_KEY_BASE;
}
function bucketKey(): string {
  return activeCity ? `${BUCKET_STORAGE_KEY_BASE}:${activeCity}` : BUCKET_STORAGE_KEY_BASE;
}
function rangeKey(): string {
  return activeCity ? `${RANGE_STORAGE_KEY_BASE}:${activeCity}` : RANGE_STORAGE_KEY_BASE;
}

/** Migration helper. The legacy un-scoped keys were written under an
 *  unknown city context — blindly copying them into whichever city loads
 *  first would re-introduce the very bug city-scoping is supposed to fix
 *  (e.g. SYD's `{lo:1000, hi:3000}` getting migrated into BLR's scoped
 *  range and then read as ₹10Cr–30Cr, defeating the price gate).
 *
 *  So: copy the legacy value across only when the caller validates it as
 *  meaningful for THIS city. Otherwise we just discard the legacy value —
 *  one-time loss of pre-upgrade picks is preferable to silently wrong
 *  filters. After the (possibly successful) migration the legacy key is
 *  cleared so it can't be re-picked-up by another city. */
function migrateLegacyKey(
  base: string,
  scoped: string,
  isValidForActiveCity: (legacy: string) => boolean,
): void {
  try {
    if (typeof localStorage === "undefined") return;
    if (localStorage.getItem(scoped) !== null) return;
    const legacy = localStorage.getItem(base);
    if (legacy === null) return;
    if (isValidForActiveCity(legacy)) {
      localStorage.setItem(scoped, legacy);
    }
    // Always clear the legacy key — keeping it would cause the next city
    // to re-evaluate the same value and potentially re-migrate it.
    localStorage.removeItem(base);
  } catch { /* storage blocked — ignore */ }
}

let current: BudgetBand = loadInitial();
const listeners = new Set<(b: BudgetBand) => void>();

/* -------------------------------------------------------------------------- */
/*  User's numeric budget range — the precise [lo,hi] the slider handles      */
/*  are at. Carries "I can afford UP TO X" intent through to the scorer.      */
/*                                                                            */
/*  Why a separate state from BudgetBucket:                                   */
/*    Buckets are a coarse discrete mapping — a user who drags from A$500k    */
/*    to A$1M collapses to ONE bucket (whichever has the largest overlap),    */
/*    losing the numeric 500–1000 fact entirely. The shortlist then ranks     */
/*    by bucket-centric scoring, which is why the live bug shows "Up to       */
/*    A$600k" picks when the user clearly said "up to A$1M".                  */
/*                                                                            */
/*    UserBudgetRange is the lossless channel. When set, auto-shortlist       */
/*    gates + grades on the exact numeric window — suburbs closest to         */
/*    `hi` score highest (realestate.com.au behaviour). When null (the user   */
/*    hasn't engaged the slider), the legacy bucket-based path runs.          */
/* -------------------------------------------------------------------------- */

export interface UserBudgetRange {
  /** Lower bound in the current (city, mode, type) unit. `0` means open-lower. */
  lo: number;
  /** Upper bound in the same unit. Snap-grid value from the range-bar. */
  hi: number;
}

let currentUserRange: UserBudgetRange | null = loadInitialUserRange();
const userRangeListeners = new Set<(r: UserBudgetRange | null) => void>();

export function getUserBudgetRange(): UserBudgetRange | null {
  return currentUserRange;
}

export function setUserBudgetRange(next: UserBudgetRange | null): void {
  // Cheap structural-equality check — skips a no-op fanout when the slider
  // re-commits at the same stops (e.g. pointerup after no movement).
  const same =
    (next === null && currentUserRange === null) ||
    (next !== null &&
      currentUserRange !== null &&
      next.lo === currentUserRange.lo &&
      next.hi === currentUserRange.hi);
  if (same) return;
  currentUserRange = next;
  try {
    if (typeof localStorage !== "undefined") {
      if (next === null) localStorage.removeItem(rangeKey());
      else localStorage.setItem(rangeKey(), JSON.stringify(next));
    }
  } catch {
    /* storage blocked — ignore, range survives for the current session */
  }
  for (const fn of Array.from(userRangeListeners)) {
    try { fn(next); } catch { /* ignore */ }
  }
}

export function subscribeUserBudgetRange(
  fn: (r: UserBudgetRange | null) => void
): () => void {
  userRangeListeners.add(fn);
  return () => {
    userRangeListeners.delete(fn);
  };
}

function loadInitialUserRange(): UserBudgetRange | null {
  try {
    if (typeof localStorage !== "undefined") {
      const raw = localStorage.getItem(rangeKey());
      if (!raw) return null;
      const v = JSON.parse(raw);
      if (
        v && typeof v === "object" &&
        typeof v.lo === "number" && typeof v.hi === "number" &&
        isFinite(v.lo) && isFinite(v.hi) && v.hi > v.lo
      ) {
        return { lo: v.lo, hi: v.hi };
      }
    }
  } catch {
    /* corrupted JSON or storage blocked — ignore */
  }
  return null;
}

/**
 * Finer bucket the user picked in the control. Display-only state —
 * downstream consumers still read `BudgetBand` (tight/moderate/stretch/
 * none), so the scoring/filter pipeline is unchanged. Persisted so the
 * user's precise budget pick survives a reload.
 */
let currentBucket: BudgetBucket = loadInitialBucket();
const bucketListeners = new Set<(b: BudgetBucket) => void>();

export function getBudgetBucket(): BudgetBucket {
  return currentBucket;
}

export function subscribeBudgetBucket(
  fn: (b: BudgetBucket) => void
): () => void {
  bucketListeners.add(fn);
  return () => {
    bucketListeners.delete(fn);
  };
}

/**
 * Set the bucket directly (used by the dropdown in the decision-lens
 * row). Resolves the mapped band for the active city/mode/type and
 * pushes it to the shared BudgetBand state so every downstream consumer
 * stays in sync.
 */
export function setBudgetBucket(
  next: BudgetBucket,
  city: CityCode
): void {
  if (next === currentBucket) return;
  currentBucket = next;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(bucketKey(), next);
    }
  } catch { /* storage blocked — ignore */ }
  const ctx = { city, mode: getPurchaseMode(), type: getPropertyType() };
  const band = budgetBucketDef(ctx, next).band;
  setBudgetBand(band);
  for (const fn of Array.from(bucketListeners)) {
    try { fn(next); } catch { /* ignore */ }
  }
}

function loadInitial(): BudgetBand {
  try {
    if (typeof localStorage !== "undefined") {
      const v = localStorage.getItem(bandKey());
      if (v === "tight" || v === "moderate" || v === "stretch" || v === "none") return v;
    }
  } catch {
    /* storage blocked — fall through */
  }
  return "none";
}

function loadInitialBucket(): BudgetBucket {
  try {
    if (typeof localStorage !== "undefined") {
      const v = localStorage.getItem(bucketKey());
      // Must list every BudgetBucket literal — missing b6/b7/b8 here means
      // users with an upper-band pick silently reset to "any" on reload.
      if (
        v === "any" ||
        v === "b0" || v === "b1" || v === "b2" || v === "b3" ||
        v === "b4" || v === "b5" || v === "b6" || v === "b7" || v === "b8"
      ) return v as BudgetBucket;
    }
  } catch {
    /* storage blocked — fall through */
  }
  return "any";
}

export function getBudgetBand(): BudgetBand {
  return current;
}

/**
 * Plain (city-agnostic) label — kept for any caller that wants the legacy
 * categorical word. UI now prefers `budgetPillLabel` from budget-format.
 */
export function getBudgetBandLabel(b: BudgetBand = current): string {
  if (b === "tight") return "Tight";
  if (b === "moderate") return "Mid-range";
  if (b === "stretch") return "Stretch";
  return "Any";
}

export function setBudgetBand(b: BudgetBand): void {
  if (b === current) return;
  current = b;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(bandKey(), b);
    }
  } catch {
    /* ignore */
  }
  for (const fn of Array.from(listeners)) {
    try {
      fn(b);
    } catch {
      /* broken subscriber must not break the rest */
    }
  }
}

export function subscribeBudgetBand(fn: (b: BudgetBand) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Bind the budget module to a specific city. Reads city-scoped storage and
 * publishes the loaded values through every existing subscriber so the UI
 * (slider, pills, shortlist, map fades) reflects state for THIS city.
 *
 * Called once per city load by `installBudgetBandControl(city)`. Idempotent:
 * a second call for the same city is a no-op.
 *
 * Side effects on city change:
 *   - Migrates any pre-fix legacy un-scoped key to the city-scoped key
 *     (one-time, per city).
 *   - Re-reads band / bucket / userRange from city-scoped storage.
 *   - Force-publishes the new values so subscribers re-render even when
 *     the value is structurally equal to the previous city's (e.g. both
 *     cities had bucket="b7" — the underlying lo/hi numbers differ).
 */
export function setBudgetCity(city: CityCode): void {
  if (activeCity === city) return;
  activeCity = city;

  // One-time migration of any legacy un-scoped values into this city's
  // scoped keys.
  //
  // Band ("none" | "tight" | "moderate" | "stretch") and Bucket ("any" |
  // "b0".."b8") are universal across cities (they're tier names and
  // bucket indices, not amounts), so any legitimate legacy value can be
  // migrated as-is — a SYD user who picked "stretch" wants the equivalent
  // tier when they hop to BLR, even if the absolute prices differ.
  //
  // Range, by contrast, carries absolute lo/hi numbers in the SOURCE
  // city's units. We can't recover the source city, so we DO NOT migrate
  // the legacy range at all — let the bucket re-derive a sensible default
  // window for the new city. Cost: a one-time loss of the user's exact
  // slider position from before the upgrade. Benefit: the price filter
  // can never silently misbehave from cross-unit bleed.
  migrateLegacyKey(STORAGE_KEY_BASE, bandKey(), (v) =>
    v === "none" || v === "tight" || v === "moderate" || v === "stretch"
  );
  migrateLegacyKey(BUCKET_STORAGE_KEY_BASE, bucketKey(), (v) =>
    v === "any" ||
    v === "b0" || v === "b1" || v === "b2" || v === "b3" ||
    v === "b4" || v === "b5" || v === "b6" || v === "b7" || v === "b8"
  );
  // Reject any legacy range value (always returns false) — the validator
  // signature still drops the legacy key as a side effect, so it doesn't
  // re-migrate to the next city the user opens.
  migrateLegacyKey(RANGE_STORAGE_KEY_BASE, rangeKey(), () => false);

  // Re-hydrate from the now-scoped keys. Force a fanout regardless of
  // structural equality — the same bucket/band literal can map to
  // different absolute lo/hi numbers in another city, and downstream
  // consumers (shortlist, range bar, verdict card) need to recompute.
  current = loadInitial();
  currentBucket = loadInitialBucket();
  currentUserRange = loadInitialUserRange();
  for (const fn of Array.from(listeners)) {
    try { fn(current); } catch { /* ignore */ }
  }
  for (const fn of Array.from(bucketListeners)) {
    try { fn(currentBucket); } catch { /* ignore */ }
  }
  for (const fn of Array.from(userRangeListeners)) {
    try { fn(currentUserRange); } catch { /* ignore */ }
  }
}

/* -------------------------------------------------------------------------- */
/*  Accent per band — mirrors constraint-priority's pattern.                  */
/* -------------------------------------------------------------------------- */

const BAND_ACCENT: Record<BudgetBand, { badge: string; badgeFg: string }> = {
  none:     { badge: "#4a4a4a", badgeFg: "#e6e6e6" },
  tight:    { badge: "#E89797", badgeFg: "#1a0e0e" },
  moderate: { badge: "#FFD37E", badgeFg: "#1a1200" },
  stretch:  { badge: "#7CDCA8", badgeFg: "#0b1a0e" },
};

export function getBudgetBandAccent(b: BudgetBand = current) {
  return BAND_ACCENT[b];
}

/* -------------------------------------------------------------------------- */
/*  Control                                                                   */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*  Row builders — the Decide surface renders these directly into its own     */
/*  sections. (The old fixed-position top-right control stack that was built  */
/*  on every boot only to be permanently CSS-hidden was deleted 2026-06-12 —  */
/*  see docs/ONE_STORY.md. Callers must invoke `setBudgetCity(city)` at boot  */
/*  before reading any budget state; the install used to do that.)            */
/* -------------------------------------------------------------------------- */

const rowShell = () => {
  const row = document.createElement("div");
  row.style.cssText = `
    display:inline-flex;flex-wrap:wrap;
    gap:3px;padding:3px;
    background:rgba(33,33,33,0.75);
    backdrop-filter:blur(10px);
    border:1px solid rgba(255,255,255,0.08);
    border-radius:999px;
    font:600 11.5px system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
    letter-spacing:0.02em;
    max-width:100%;
  `;
  return row;
};

/**
 * Buy / Rent radiogroup, store-wired. Build once per page (the subscription
 * lives for the session, matching the persistent Decide surface DOM).
 */
export function buildModeRow(): HTMLElement {
  const modeRow = rowShell();
  modeRow.setAttribute("role", "radiogroup");
  modeRow.setAttribute("aria-label", "Buy or Rent");
  const modePills: { v: PurchaseMode; btn: HTMLButtonElement }[] = [];

  const paintMode = () => {
    const cur = getPurchaseMode();
    for (const { v, btn } of modePills) {
      const active = v === cur;
      btn.setAttribute("aria-checked", String(active));
      btn.style.background = active ? "#FFD37E" : "transparent";
      btn.style.color = active ? "#1a1200" : "#d6d6d6";
      btn.style.fontWeight = active ? "700" : "500";
      btn.style.cursor = active ? "default" : "pointer";
      btn.style.boxShadow = active ? "0 1px 3px rgba(0,0,0,0.35)" : "none";
    }
  };

  for (const v of ["buy", "rent"] as const) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.setAttribute("role", "radio");
    btn.textContent = v === "buy" ? "Buy" : "Rent";
    btn.style.cssText = `
      border:0;padding:5px 11px;border-radius:999px;
      font:inherit;transition:background 140ms ease,color 140ms ease;
    `;
    btn.addEventListener("click", () => setPurchaseMode(v));
    btn.addEventListener("mouseenter", () => {
      if (v !== getPurchaseMode()) btn.style.background = "rgba(255,255,255,0.08)";
    });
    btn.addEventListener("mouseleave", () => {
      if (v !== getPurchaseMode()) btn.style.background = "transparent";
    });
    modePills.push({ v, btn });
    modeRow.appendChild(btn);
  }

  paintMode();
  subscribePurchaseMode(() => paintMode());
  return modeRow;
}

/**
 * Any / House / Apartment radiogroup, store-wired. Same lifecycle contract
 * as `buildModeRow`.
 */
export function buildTypeRow(): HTMLElement {
  const typeRow = rowShell();
  typeRow.setAttribute("role", "radiogroup");
  typeRow.setAttribute("aria-label", "Property type");
  const typePills: { v: PropertyType; btn: HTMLButtonElement }[] = [];

  const paintType = () => {
    const cur = getPropertyType();
    for (const { v, btn } of typePills) {
      const active = v === cur;
      btn.setAttribute("aria-checked", String(active));
      btn.style.background = active ? "#FFD37E" : "transparent";
      btn.style.color = active ? "#1a1200" : "#d6d6d6";
      btn.style.fontWeight = active ? "700" : "500";
      btn.style.cursor = active ? "default" : "pointer";
      btn.style.boxShadow = active ? "0 1px 3px rgba(0,0,0,0.35)" : "none";
    }
  };

  for (const v of ["any", "house", "apartment"] as const) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.setAttribute("role", "radio");
    btn.textContent =
      v === "any" ? "Any" : v === "house" ? "House" : "Apartment";
    btn.style.cssText = `
      border:0;padding:5px 11px;border-radius:999px;
      font:inherit;transition:background 140ms ease,color 140ms ease;
    `;
    btn.addEventListener("click", () => setPropertyType(v));
    btn.addEventListener("mouseenter", () => {
      if (v !== getPropertyType()) btn.style.background = "rgba(255,255,255,0.08)";
    });
    btn.addEventListener("mouseleave", () => {
      if (v !== getPropertyType()) btn.style.background = "transparent";
    });
    typePills.push({ v, btn });
    typeRow.appendChild(btn);
  }

  paintType();
  subscribePropertyType(() => paintType());
  return typeRow;
}
